<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16">
    <div class="container max-width-mobile">
      <cf-validated-form
        name="signatureForm"
        :extra-validations="validateSignatureRequirements"
        @valid-submit="handleSignatureComplete"
      >
        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <div class="cf-flex cf-flex--column cf-flex--gap-16">
            <h3 class="cf-title cf-title--3">Signature</h3>

            <div class="signature-pad-container">
              <vue-signature-pad
                ref="signaturePad"
                width="100%"
                height="133px"
                :options="{
                  backgroundColor: 'rgb(255,255,255)',
                  penColor: 'rgb(0, 0, 0)',
                  minWidth: 2,
                  maxWidth: 2,
                  onEnd: onSignatureEnd
                }"
                class="signature-pad"
              />

              <div class="signature-pad-controls">
                <button
                  class="cf-button cf-button--secondary cf-button--sm"
                  type="button"
                  @click="clearSignature"
                >
                  Clear
                </button>
              </div>
            </div>

            <cf-text-input
              v-model="fullName"
              :validations="signatureFieldValidations"
              label="Full Name"
              name="signatureFullName"
              type="text"
              placeholder="Enter your full name"
            />

            <cf-input-error
              v-if="signatureError"
              :error="signatureError"
            />
          </div>
        </div>
      </cf-validated-form>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { VueSignaturePad } from 'vue-signature-pad';

  import validateFullName from '@/helpers/validate-full-name';
  import CfInputError from '@/view/components/shared/cf-input-error/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';

  export default {
    name: 'CfSignatureBlock',

    components: {
      CfInputError,
      CfTextInput,
      CfValidatedForm,
      VueSignaturePad
    },

    data() {
      return {
        fullName: '',
        hasSignature: false,
        signatureError: null
      };
    },

    computed: {
      ...mapGetters('orders', ['order']),

      signatureFieldValidations() {
        return {
          required: true,
          full_name: true
        };
      },

      isSignatureComplete() {
        const hasFullName = validateFullName(this.fullName) === true && this.fullName.trim().length > 0;

        return this.hasSignature && hasFullName;
      }
    },

    watch: {
      isSignatureComplete(newValue) {
        if (newValue) {
          const signatureData = this.getSignatureData();
          this.$emit('signature-complete', signatureData);
        }
        else {
          this.$emit('signature-complete', null);
        }
      },

      fullName() {
        this.emitSignatureData();
      }
    },

    mounted() {
      this.emitSignatureData();
    },

    methods: {
      clearSignature() {
        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.clearSignature();
          this.signatureError = null;
        }
        this.hasSignature = false;
        this.emitSignatureData();
      },

      onSignatureEnd() {
        this.hasSignature = !!this.$refs.signaturePad && !this.$refs.signaturePad.isEmpty();
        this.emitSignatureData();
      },

      emitSignatureData() {
        if (this.isSignatureComplete) {
          const signatureData = this.getSignatureData();
          this.$emit('signature-complete', signatureData);
        }
        else {
          this.$emit('signature-complete', null);
        }
      },

      async validateSignatureRequirements() {
        // This is the extra validation method used by cf-validated-form
        // It validates the signature pad specifically since vee-validate can't handle it
        this.signatureError = null;

        if (!this.$refs.signaturePad || this.$refs.signaturePad.isEmpty()) {
          this.signatureError = 'Signature is required.';
          return false;
        }

        return true;
      },

      handleSignatureComplete() {
        // Called when cf-validated-form emits valid-submit
        const signatureData = this.getSignatureData();
        this.$emit('signature-complete', signatureData);
      },

      getSignatureData() {
        if (!this.isSignatureComplete) {
          return null;
        }

        // Get the signature as base64
        const signatureData = this.$refs.signaturePad.saveSignature();
        // Extract base64 data (remove data:image/png;base64, prefix)

        const base64Data = signatureData.data.split(',')[1];

        return {
          orderId: this.order.orderId,
          assetType: 'Signature',
          fileName: `signature_${this.fullName.replace(/\s+/g, '_')}_${Date.now()}.png`,
          contentType: 'image/png',
          fileBase64: base64Data
        };
      }
    }
  };
</script>

<style lang="scss" scoped>
  .signature-pad-container {
    position: relative;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
  }

  .signature-pad {
    width: 100%;
    height: 133px;
    border-radius: 4px;
  }

  .signature-pad-controls {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  @media (max-width: 768px) {
    .signature-pad {
      height: 100px;
    }
  }
</style>
